import { useEffect, useState } from "react";
import EventsLayout from "@/components/layouts/EventLayout";
import TabbedPage from "@/components/shared/TabbedPage";
import { Button } from "@/components/ui";
import { usePage, router } from "@inertiajs/react";
import { PlusCircleFilled, MailOutlined, InteractionOutlined } from "@ant-design/icons";
import { message } from "antd";
import ManualEmailList from "./components/ManualEmailList";
import AutomatedEmailList from "./components/AutomatedEmailList";
import EmailPreviewModal from "./components/EmailPreviewModal";
import SendConfirmModal from "./components/SendConfirmModal";
import {
  type ManualEmail,
  type AutomatedEmail,
  type AutomatedEmailAudience,
  type Event,
  DEFAULT_AUTOMATED_EMAIL_AUDIENCE,
  E_CERTIFICATE_TEMPLATE_TYPE,
  defaultEmailHtmlContent,
  getAutomatedEmailTitle,
} from "./types";

/**
 * Who a manual send is addressed to, in the words the confirmation uses.
 * Mirrors AutomatedEmailBlastJob#recipients_for: approved registrants, narrowed
 * to those who checked in when the template is attended-only.
 */
const SEND_AUDIENCE_COPY: Record<AutomatedEmailAudience, string> = {
  all: "every approved registrant",
  attended: "approved registrants who checked in",
};

type PageProps = {
  event: Event;
  manualEmails?: ManualEmail[];
  automatedEmails?: AutomatedEmail[];
};

const EmailBlastsIndex = () => {
  const {
    event,
    manualEmails: manualEmailsProp = [],
    automatedEmails: automatedEmailsProp = [],
  } = usePage<PageProps>().props;

  const [manualEmails, setManualEmails] = useState<ManualEmail[]>(manualEmailsProp);
  const [automatedEmails, setAutomatedEmails] = useState<AutomatedEmail[]>(automatedEmailsProp);

  // Modal states
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [sendConfirmModalOpen, setSendConfirmModalOpen] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState<ManualEmail | null>(null);
  const [selectedAutomatedEmail, setSelectedAutomatedEmail] = useState<AutomatedEmail | null>(null);
  const [automatedSendTarget, setAutomatedSendTarget] = useState<AutomatedEmail | null>(null);
  const [automatedSendLoading, setAutomatedSendLoading] = useState(false);

  useEffect(() => {
    setManualEmails(manualEmailsProp);
  }, [manualEmailsProp]);

  useEffect(() => {
    setAutomatedEmails(automatedEmailsProp);
  }, [automatedEmailsProp]);

  const handleCreateEmail = () => {
    window.location.href = `/events/${event.slug}/messaging/email_blasts/new`;
  };

  const handleSendEmail = (email: ManualEmail) => {
    setSelectedEmail(email);
    setSendConfirmModalOpen(true);
  };

  const handleConfirmSend = () => {
    if (selectedEmail) {
      router.post(
        `/events/${event.slug}/messaging/email_blasts/${selectedEmail.id}/send_now`,
        {},
        {
          preserveScroll: true,
          onSuccess: () => {
            setSendConfirmModalOpen(false);
            setSelectedEmail(null);
          },
          onError: () => {
            message.error("Failed to send email.");
            setSendConfirmModalOpen(false);
            setSelectedEmail(null);
          },
        }
      );
    }
  };

  const handleDuplicateEmail = (email: ManualEmail) => {
    router.post(
      `/events/${event.slug}/messaging/email_blasts/${email.id}/duplicate`,
      {},
      {
        preserveScroll: true,
        onError: () => message.error("Failed to duplicate email."),
      }
    );
  };

  const handleDeleteEmail = (email: ManualEmail) => {
    router.delete(`/events/${event.slug}/messaging/email_blasts/${email.id}`, {
      preserveScroll: true,
      onError: () => message.error("Failed to delete email."),
    });
  };

  const handleToggleAutomatedEmail = (id: string, enabled: boolean) => {
    router.patch(
      `/events/${event.slug}/messaging/email_blasts/automated/${id}`,
      { automated_email_template: { enabled } },
      {
        preserveScroll: true,
        onSuccess: () => {
          setAutomatedEmails((prev) => prev.map((e) => (e.id === id ? { ...e, enabled } : e)));
        },
        onError: () => message.error("Failed to update automated email."),
      }
    );
  };

  const handlePreviewAutomatedEmail = (email: AutomatedEmail) => {
    setSelectedAutomatedEmail(email);
    setPreviewModalOpen(true);
  };

  const handleSendAutomatedEmail = (email: AutomatedEmail) => {
    setAutomatedSendTarget(email);
  };

  const closeAutomatedSendModal = () => {
    setAutomatedSendTarget(null);
    setAutomatedSendLoading(false);
  };

  const handleConfirmSendAutomated = () => {
    if (!automatedSendTarget) return;

    setAutomatedSendLoading(true);
    // The server answers every outcome with a redirect and a flash — including
    // its refusals, which FlashMessages surfaces. onError is left for the
    // request itself failing.
    router.post(
      `/events/${event.slug}/messaging/email_blasts/automated/${automatedSendTarget.id}/send`,
      {},
      {
        preserveScroll: true,
        onError: () => message.error("Failed to send this automated email."),
        onFinish: closeAutomatedSendModal,
      }
    );
  };

  const handleEditAutomatedEmail = (email: AutomatedEmail) => {
    // E-certificates are edited on their own two-tab page. The server would
    // redirect there anyway — `edit_automated` does it for the sake of old deep
    // links — but going straight there saves the round trip.
    window.location.href =
      email.templateType === E_CERTIFICATE_TEMPLATE_TYPE
        ? `/events/${event.slug}/certificates`
        : `/events/${event.slug}/messaging/email_blasts/automated/${email.id}/edit`;
  };

  // Handle URL query params for tab selection
  const getDefaultTab = () => {
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      return params.get("tab") || "manual";
    }
    return "manual";
  };

  const tabs = [
    {
      key: "manual",
      label: "Manual Email",
      icon: <MailOutlined />,
      children: (
        <ManualEmailList
          emails={manualEmails}
          eventSlug={event.slug}
          onSend={handleSendEmail}
          onDuplicate={handleDuplicateEmail}
          onDelete={handleDeleteEmail}
        />
      ),
    },
    {
      key: "automated",
      label: "Automated Email",
      icon: <InteractionOutlined />,
      children: (
        <AutomatedEmailList
          emails={automatedEmails}
          eventSlug={event.slug}
          onToggle={handleToggleAutomatedEmail}
          onPreview={handlePreviewAutomatedEmail}
          onEdit={handleEditAutomatedEmail}
          onSend={handleSendAutomatedEmail}
        />
      ),
    },
  ];

  return (
    <EventsLayout>
      <TabbedPage
        title="Email Blast"
        subtitle="Select from default email templates or create your own using the built-in email builder."
        headerAction={
          <div className="flex gap-3">
            <Button
              variant="primary"
              icon={<PlusCircleFilled />}
              onClick={handleCreateEmail}
            >
              Create Manual Email
            </Button>
          </div>
        }
        tabs={tabs}
        defaultActiveKey={getDefaultTab()}
      />

      {/* Preview Modal for Automated Emails */}
      {selectedAutomatedEmail && (
        <EmailPreviewModal
          open={previewModalOpen}
          onClose={() => {
            setPreviewModalOpen(false);
            setSelectedAutomatedEmail(null);
          }}
          email={{
            campaignName: getAutomatedEmailTitle(selectedAutomatedEmail),
            subjectLine: selectedAutomatedEmail.subjectLine,
            senderName: selectedAutomatedEmail.senderName,
            senderEmail: selectedAutomatedEmail.senderEmail,
            recipientCount: 0,
            htmlContent: selectedAutomatedEmail.htmlContent || defaultEmailHtmlContent,
            updatedAt: new Date().toISOString(),
          }}
          mode="view"
        />
      )}

      {/* Send Confirmation Modal */}
      <SendConfirmModal
        open={sendConfirmModalOpen}
        onClose={() => {
          setSendConfirmModalOpen(false);
          setSelectedEmail(null);
        }}
        onConfirm={handleConfirmSend}
        campaignName={selectedEmail?.campaignName || ""}
        recipientCount={selectedEmail?.recipientCount || 0}
      />

      {/* Send Confirmation for a manual-send automated email */}
      <SendConfirmModal
        open={Boolean(automatedSendTarget)}
        onClose={closeAutomatedSendModal}
        onConfirm={handleConfirmSendAutomated}
        campaignName={getAutomatedEmailTitle(automatedSendTarget)}
        loading={automatedSendLoading}
        description={
          <>
            <p className="mt-2">
              Goes to{" "}
              {
                SEND_AUDIENCE_COPY[
                  automatedSendTarget?.audience || DEFAULT_AUTOMATED_EMAIL_AUDIENCE
                ]
              }
              .
            </p>
            <p className="mt-2">
              Anyone who already received this email is skipped. Everyone else is sent it,
              including addresses an earlier send failed on — which will fail again if the
              address is not deliverable.
            </p>
          </>
        }
      />
    </EventsLayout>
  );
};

export default EmailBlastsIndex;
